import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaignStats, resetCampaignStats } from "../../redux/newsletterSlice";
import "./CampaignStats.css";

const PROVIDER_LABELS = {
  gmail: "Gmail",
  hotmail: "Hotmail",
  yahoo: "Yahoo",
  apple: "Apple",
  proton: "Proton",
  otherFree: "Otros",
  corporate: "Corporativo",
};

const PROVIDER_COLORS = {
  gmail: "#ea4335",
  hotmail: "#0078d4",
  yahoo: "#720e9e",
  apple: "#a2aaad",
  proton: "#6d4aff",
  otherFree: "#888",
  corporate: "#00e0ff",
};

function ProviderBars({ providers }) {
  const active = Object.entries(providers || {}).filter(([, v]) => v > 0);
  if (active.length === 0) return <span className="cs-no-data">Sin datos</span>;

  const total = active.reduce((sum, [, v]) => sum + v, 0);

  return (
    <div className="cs-providers">
      {active.map(([key, value]) => (
        <div key={key} className="cs-provider-row">
          <span className="cs-provider-label">{PROVIDER_LABELS[key] || key}</span>
          <div className="cs-bar-track">
            <div
              className="cs-bar-fill"
              style={{
                width: `${Math.round((value / total) * 100)}%`,
                background: PROVIDER_COLORS[key] || "#00e0ff",
              }}
            />
          </div>
          <span className="cs-provider-count">{value}</span>
        </div>
      ))}
    </div>
  );
}

function CampaignCard({ campaign }) {
  const sentAt = new Date(campaign.sentAt).toLocaleString("es-CO", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const rateColor =
    campaign.openRate >= 75
      ? "rgba(102, 255, 106, 1)"
      : campaign.openRate >= 40
      ? "rgba(255, 161, 61, 1)"
      : "rgba(241, 35, 35, 1)";

  return (
    <div className="cs-card">
      <div className="cs-card-header">
        <h3 className="cs-subject">{campaign.subject}</h3>
        <div className="cs-meta">
          <span className="cs-badge">{String(campaign.topic || "").replace(/_/g, " ")}</span>
          {campaign.language && (
            <span className="cs-badge cs-badge--lang">{campaign.language}</span>
          )}
          {campaign.status === "sending" && (
            <span className="cs-badge">Enviando…</span>
          )}
          <span className="cs-date">{sentAt}</span>
        </div>
      </div>

      <div className="cs-card-body">
        <div className="cs-stats-grid">
          <div className="cs-stat">
            <span className="cs-stat-value">{campaign.recipientsCount}</span>
            <span className="cs-stat-label">Destinatarios</span>
          </div>
          <div className="cs-stat">
            <span className="cs-stat-value">{campaign.sentCount ?? campaign.recipientsCount}</span>
            <span className="cs-stat-label">Enviados</span>
          </div>
          {(campaign.failedCount > 0 || campaign.bouncedCount > 0) && (
            <div className="cs-stat">
              <span className="cs-stat-value">
                {(campaign.failedCount || 0) + (campaign.bouncedCount || 0)}
              </span>
              <span className="cs-stat-label">Fallidos / rebotes</span>
            </div>
          )}
          <div className="cs-stat">
            <span className="cs-stat-value">{campaign.uniqueOpens}</span>
            <span className="cs-stat-label">Aperturas únicas</span>
          </div>
          <div className="cs-stat">
            <span className="cs-stat-value">{campaign.totalOpens}</span>
            <span className="cs-stat-label">Aperturas totales</span>
          </div>
          <div className="cs-stat cs-stat--rate">
            <span className="cs-stat-value" style={{ color: rateColor }}>
              {campaign.openRate}%
            </span>
            <span className="cs-stat-label">Tasa de apertura</span>
            <div className="cs-rate-track">
              <div
                className="cs-rate-fill"
                style={{ width: `${campaign.openRate}%`, background: rateColor }}
              />
            </div>
          </div>
        </div>

        <div className="cs-divider" />

        <div className="cs-providers-section">
          <span className="cs-providers-title">Por proveedor</span>
          <ProviderBars providers={campaign.recipientsByProvider} />
        </div>
      </div>
    </div>
  );
}

export default function CampaignStats() {
  const dispatch = useDispatch();
  const { campaignStats, campaignStatsStatus, campaignStatsError } = useSelector(
    (state) => state.newsletter
  );

  useEffect(() => {
    dispatch(fetchCampaignStats());
    return () => {
      dispatch(resetCampaignStats());
    };
  }, [dispatch]);

  if (campaignStatsStatus === "idle" || campaignStatsStatus === "loading") {
    return (
      <div className="cs-center">
        <div className="cs-spinner" />
        <span>Cargando estadísticas...</span>
      </div>
    );
  }

  if (campaignStatsStatus === "failed") {
    return (
      <div className="cs-center cs-error">
        Error al cargar estadísticas: {campaignStatsError}
      </div>
    );
  }

  if (!campaignStats.length) {
    return <div className="cs-center">No hay campañas registradas.</div>;
  }

  return (
    <div className="cs-root">
      <div className="cs-grid">
        {campaignStats.map((c) => (
          <CampaignCard key={c.campaignId} campaign={c} />
        ))}
      </div>
    </div>
  );
}
